import React, { useCallback, useMemo, useState } from "react";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  ReactFlowProvider,
  addEdge,
  useEdgesState,
  useNodesState,
  Handle,
  Position,
} from "reactflow";
import { Plus, Diamond, Circle, Square as SquareIcon, Trash2, ShieldAlert } from "lucide-react";

/** Custom glass node types */
function GlassNode({ data, type }) {
  const styleByType = {
    start: "rounded-full bg-emerald-500/20 border-emerald-400/50 text-emerald-200",
    end: "rounded-full bg-rose-500/20 border-rose-400/50 text-rose-200",
    process: "rounded-2xl bg-white/[0.06] border-white/20 text-white",
    decision: "bg-amber-500/15 border-amber-400/50 text-amber-100 rotate-45",
    group: "rounded-3xl bg-indigo-500/5 border-dashed border-indigo-500/30 text-indigo-200 min-h-[140px] min-w-[200px]"
  };
  
  const isDecision = type === "decision";
  const isGroup = type === "group";
  
  const inner = (
    <div
      className={`min-w-[130px] px-4 py-3 backdrop-blur-xl border shadow-[0_10px_30px_-10px_rgba(0,0,0,0.6)] text-sm font-medium text-center ${
        styleByType[type] || styleByType.process
      }`}
    >
      <span className={isDecision ? "-rotate-45 inline-block" : ""}>{data.label}</span>
      {isGroup && (
        <div className="text-[10px] text-[#9AA4B2] mt-2 border-t border-white/5 pt-1 font-normal uppercase tracking-wider">
          Process Group boundary
        </div>
      )}
    </div>
  );
  return (
    <>
      <Handle type="target" position={Position.Top} className="!bg-indigo-400 !w-2 !h-2" />
      {inner}
      <Handle type="source" position={Position.Bottom} className="!bg-indigo-400 !w-2 !h-2" />
    </>
  );
}

const nodeTypes = {
  start: (p) => <GlassNode {...p} type="start" />,
  end: (p) => <GlassNode {...p} type="end" />,
  process: (p) => <GlassNode {...p} type="process" />,
  decision: (p) => <GlassNode {...p} type="decision" />,
  group: (p) => <GlassNode {...p} type="group" />,
};

function normalizeToRF(input) {
  const nodes = (input?.nodes || []).map((n) => ({
    id: n.id,
    type: n.type || "process",
    position: { x: n.x ?? n.position?.x ?? 100, y: n.y ?? n.position?.y ?? 100 },
    data: { label: n.label ?? n.data?.label ?? "Step" },
  }));
  const edges = (input?.edges || []).map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    label: e.label,
    animated: true,
    style: { stroke: "#4F46E5" },
  }));
  return { nodes, edges };
}

function serialize(nodes, edges) {
  return {
    nodes: nodes.map((n) => ({
      id: n.id,
      type: n.type,
      x: n.position.x,
      y: n.position.y,
      label: n.data.label,
    })),
    edges: edges.map((e) => ({ id: e.id, source: e.source, target: e.target, label: e.label })),
  };
}

function FlowchartInner({ initial, onChange, readOnly }) {
  const initialData = useMemo(() => normalizeToRF(initial), [initial]);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialData.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialData.edges);
  const [selectedNode, setSelectedNode] = useState(null);

  const emit = useCallback(
    (ns, es) => {
      if (onChange) onChange(serialize(ns, es));
    },
    [onChange]
  );

  const onConnect = useCallback(
    (params) => {
      setEdges((eds) => {
        const next = addEdge({ ...params, animated: true, style: { stroke: "#4F46E5" } }, eds);
        emit(nodes, next);
        return next;
      });
    },
    [nodes, setEdges, emit]
  );

  const addNode = (type) => {
    const id = `n_${Date.now()}`;
    const label = { start: "Start", end: "End", process: "New step", decision: "Decision?", group: "Operator Group" }[type] || "Step";
    const newNode = {
      id,
      type,
      position: { x: 200 + Math.random() * 120, y: 150 + Math.random() * 120 },
      data: { label },
    };
    setNodes((ns) => {
      const next = ns.concat(newNode);
      emit(next, edges);
      return next;
    });
  };

  const clearFlow = () => {
    setNodes([]);
    setEdges([]);
    setSelectedNode(null);
    emit([], []);
  };

  // Select node on click to open editor panel
  const onNodeClick = (_, node) => {
    setSelectedNode(node);
  };

  const onPaneClick = () => {
    setSelectedNode(null);
  };

  // renaming: double click fallback prompt
  const onNodeDoubleClick = (_, node) => {
    const nextLabel = window.prompt("Rename Step Label:", node.data.label);
    if (nextLabel == null) return;
    setNodes((ns) => {
      const next = ns.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, label: nextLabel } } : n));
      emit(next, edges);
      return next;
    });
  };

  // propagate drag/delete changes
  const handleNodesChange = (chs) => {
    onNodesChange(chs);
    setTimeout(() => {
      setNodes((ns) => {
        emit(ns, edges);
        return ns;
      });
    }, 0);
  };
  const handleEdgesChange = (chs) => {
    onEdgesChange(chs);
    setTimeout(() => {
      setEdges((es) => {
        emit(nodes, es);
        return es;
      });
    }, 0);
  };

  return (
    <div className="w-full h-[560px] rounded-3xl overflow-hidden glass relative" data-testid="flowchart-canvas">
      {!readOnly && (
        <div className="absolute top-3 left-3 z-10 flex flex-wrap gap-1 glass rounded-full p-1.5 max-w-[90%]">
          <button
            type="button"
            onClick={() => addNode("start")}
            className="px-3 py-1.5 rounded-full text-xs text-emerald-300 hover:bg-white/5 flex items-center gap-1"
            data-testid="flow-add-start"
          >
            <Circle size={11} /> Start
          </button>
          <button
            type="button"
            onClick={() => addNode("process")}
            className="px-3 py-1.5 rounded-full text-xs text-white hover:bg-white/5 flex items-center gap-1"
            data-testid="flow-add-process"
          >
            <SquareIcon size={11} /> Step
          </button>
          <button
            type="button"
            onClick={() => addNode("decision")}
            className="px-3 py-1.5 rounded-full text-xs text-amber-200 hover:bg-white/5 flex items-center gap-1"
            data-testid="flow-add-decision"
          >
            <Diamond size={11} /> Decision
          </button>
          <button
            type="button"
            onClick={() => addNode("group")}
            className="px-3 py-1.5 rounded-full text-xs text-indigo-300 hover:bg-white/5 flex items-center gap-1"
          >
            <Plus size={11} /> Group Container
          </button>
          <button
            type="button"
            onClick={() => addNode("end")}
            className="px-3 py-1.5 rounded-full text-xs text-rose-300 hover:bg-white/5 flex items-center gap-1"
            data-testid="flow-add-end"
          >
            <Circle size={11} /> End
          </button>
          <button
            type="button"
            onClick={clearFlow}
            className="px-3 py-1.5 rounded-full text-xs text-[#9AA4B2] hover:text-white hover:bg-white/5 flex items-center gap-1"
            data-testid="flow-clear"
          >
            <Trash2 size={11} /> Clear
          </button>
        </div>
      )}

      {/* Selected Node Editor Settings Floating Panel */}
      {selectedNode && !readOnly && (
        <div className="absolute top-16 right-3 z-10 glass-strong p-4 rounded-2xl w-64 border border-white/10 shadow-2xl">
          <div className="flex justify-between items-center mb-3">
            <h4 className="text-[11px] uppercase tracking-widest text-[#9AA4B2] font-bold">Edit Selected Step</h4>
            <button 
              type="button"
              onClick={() => setSelectedNode(null)} 
              className="text-[#5B6472] hover:text-white text-xs font-semibold"
            >
              ✕
            </button>
          </div>
          
          <div className="space-y-3.5 text-xs">
            <div>
              <label className="block text-[#9AA4B2] mb-1 font-semibold text-[10px] uppercase">Step Label</label>
              <input
                type="text"
                value={selectedNode.data.label || ""}
                onChange={(e) => {
                  const newLabel = e.target.value;
                  setSelectedNode(prev => ({ ...prev, data: { ...prev.data, label: newLabel } }));
                  setNodes((ns) => {
                    const next = ns.map((n) => (n.id === selectedNode.id ? { ...n, data: { ...n.data, label: newLabel } } : n));
                    emit(next, edges);
                    return next;
                  });
                }}
                className="w-full bg-[#05070D]/80 border border-white/10 rounded-xl p-2.5 text-white focus:outline-none focus:border-indigo-400 transition"
              />
            </div>
            
            <div>
              <label className="block text-[#9AA4B2] mb-1 font-semibold text-[10px] uppercase">Step Shape Style</label>
              <select
                value={selectedNode.type || "process"}
                onChange={(e) => {
                  const newType = e.target.value;
                  setSelectedNode(prev => ({ ...prev, type: newType }));
                  setNodes((ns) => {
                    const next = ns.map((n) => (n.id === selectedNode.id ? { ...n, type: newType } : n));
                    emit(next, edges);
                    return next;
                  });
                }}
                className="w-full bg-[#05070D]/80 border border-white/10 rounded-xl p-2.5 text-white focus:outline-none focus:border-indigo-400 transition"
              >
                <option value="start">Start (Green Circle)</option>
                <option value="process">Standard Step (Glass Card)</option>
                <option value="decision">Decision Node (Diamond)</option>
                <option value="group">Operator Group Boundary (Large Box)</option>
                <option value="end">End (Rose Circle)</option>
              </select>
            </div>
            
            <div className="pt-1.5 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  const toDeleteId = selectedNode.id;
                  setNodes((ns) => {
                    const next = ns.filter((n) => n.id !== toDeleteId);
                    emit(next, edges.filter(e => e.source !== toDeleteId && e.target !== toDeleteId));
                    return next;
                  });
                  setEdges((es) => {
                    return es.filter(e => e.source !== toDeleteId && e.target !== toDeleteId);
                  });
                  setSelectedNode(null);
                }}
                className="w-full bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/30 text-rose-300 py-2.5 rounded-xl flex items-center justify-center gap-1.5 font-bold transition"
              >
                <Trash2 size={13} />
                Delete Selected Step
              </button>
            </div>
          </div>
        </div>
      )}

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onNodeDoubleClick={onNodeDoubleClick}
        fitView
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#4F46E5" gap={22} size={1} style={{ opacity: 0.25 }} />
        <MiniMap
          className="!bg-[#0A0E1A]/70 !border !border-white/10 !rounded-2xl"
          nodeColor={() => "#4F46E5"}
          maskColor="rgba(0,0,0,0.6)"
        />
        <Controls className="!bg-[#0A0E1A]/70 !border-white/10 !rounded-2xl [&_button]:!bg-transparent [&_button]:!text-white [&_button:hover]:!bg-white/10" />
      </ReactFlow>

      {!readOnly && (
        <div className="absolute bottom-3 right-3 text-[10px] text-[#5B6472] px-3.5 py-2 rounded-full glass">
          <span className="flex items-center gap-1">
            <Plus size={11} /> Click step node to select & edit/delete · Drag handle to connect
          </span>
        </div>
      )}
    </div>
  );
}

export function FlowchartCanvas(props) {
  return (
    <ReactFlowProvider>
      <FlowchartInner {...props} />
    </ReactFlowProvider>
  );
}
